import React, { useState } from 'react';
import { ProductVariant } from '../../../lib/api/models';
import { Plus, Trash2, Edit2, Image, X, ChevronUp, ChevronDown } from 'lucide-react';
import {
  createEmptyProductPrices,
  NON_BASE_TARIFA_OPTIONS,
  normalizeProductPrices,
  TARIFA_OPTIONS,
  TarifaKey
} from '../../../lib/utils/tarifas';

interface ProductVariantsFormProps {
  variants: ProductVariant[];
  onChange: (variants: ProductVariant[]) => void;
  userRole?: string;
}

export const ProductVariantsForm: React.FC<ProductVariantsFormProps> = ({
  variants,
  onChange,
  userRole = 'admin'
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);

  const addVariant = () => {
    const newVariant: ProductVariant = {
      name: '',
      sku: '',
      price: 0,
      prices: createEmptyProductPrices(),
      promoPrices: createEmptyProductPrices(),
      stock: 0,
      isActive: true,
      attributes: {
        size: '',
        unit: '',
        packaging: ''
      },
      packaging: {
        presentation: '',
        minimumQuantity: 1,
        quantityStep: 1,
        description: ''
      }
    };
    onChange([...variants, newVariant]);
    setEditingIndex(variants.length);
    setShowForm(true);
  };

  const updateVariant = (index: number, updatedVariant: ProductVariant) => {
    const newVariants = [...variants];
    newVariants[index] = updatedVariant;
    onChange(newVariants);
    setEditingIndex(null);
    setShowForm(false);
  };

  const deleteVariant = (index: number) => {
    const newVariants = variants.filter((_, i) => i !== index);
    onChange(newVariants);
  };

  const moveVariantUp = (index: number) => {
    if (index === 0) return;
    const newVariants = [...variants];
    [newVariants[index - 1], newVariants[index]] = [newVariants[index], newVariants[index - 1]];
    onChange(newVariants);
  };

  const moveVariantDown = (index: number) => {
    if (index === variants.length - 1) return;
    const newVariants = [...variants];
    [newVariants[index], newVariants[index + 1]] = [newVariants[index + 1], newVariants[index]];
    onChange(newVariants);
  };

  const startEdit = (index: number) => {
    setEditingIndex(index);
    setShowForm(true);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setShowForm(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Variantes del Producto</h3>
        <button
          type="button"
          onClick={addVariant}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
        >
          <Plus className="h-4 w-4 mr-1" />
          Anadir Variante
        </button>
      </div>

      {variants.length > 0 && (
        <div className="space-y-2">
          {variants.map((variant, index) => (
            <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center space-x-4">
                  {variant.images && variant.images.length > 0 ? (
                    <img
                      src={variant.images[0].url}
                      alt={variant.images[0].alt || variant.name}
                      className="w-16 h-16 object-cover rounded-md border border-gray-200"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-100 rounded-md flex items-center justify-center">
                      <Image className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-medium text-gray-900">{variant.name || 'Sin nombre'}</h4>
                    <p className="text-sm text-gray-500">SKU: {variant.sku || 'Sin SKU'}</p>
                    {variant.images && variant.images.length > 0 && (
                      <p className="text-xs text-gray-400">{variant.images.length} imagen(es)</p>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Precio: {variant.price ? `${variant.price.toFixed(2)} EUR` : 'No definido'}</p>
                    <p>Stock: {variant.stock || 0} unidades</p>
                  </div>
                  {variant.attributes && (
                    <div className="text-sm text-gray-500">
                      {variant.attributes.size && <p>Tamano: {variant.attributes.size}</p>}
                      {variant.attributes.unit && <p>Unidad: {variant.attributes.unit}</p>}
                      {variant.attributes.packaging && <p>Envase: {variant.attributes.packaging}</p>}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  type="button"
                  onClick={() => moveVariantUp(index)}
                  disabled={index === 0}
                  className={`p-2 ${index === 0 ? 'text-gray-200 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'}`}
                  title="Subir"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => moveVariantDown(index)}
                  disabled={index === variants.length - 1}
                  className={`p-2 ${index === variants.length - 1 ? 'text-gray-200 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600'}`}
                  title="Bajar"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => startEdit(index)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="Editar"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => deleteVariant(index)}
                  className="p-2 text-red-400 hover:text-red-600"
                  title="Eliminar"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && editingIndex !== null && (
        <VariantForm
          variant={variants[editingIndex]}
          onSave={(updatedVariant) => updateVariant(editingIndex, updatedVariant)}
          onCancel={cancelEdit}
          userRole={userRole}
        />
      )}
    </div>
  );
};

interface VariantFormProps {
  variant: ProductVariant;
  onSave: (variant: ProductVariant) => void;
  onCancel: () => void;
  userRole: string;
}

const VariantForm: React.FC<VariantFormProps> = ({ variant, onSave, onCancel, userRole }) => {
  const [formData, setFormData] = useState<ProductVariant>({
    ...variant,
    attributes: variant.attributes || { size: '', unit: '', packaging: '' },
    prices: normalizeProductPrices(variant.prices, variant.price || 0),
    promoPrices: normalizeProductPrices(variant.promoPrices),
    images: variant.images || [],
    packaging: variant.packaging || { presentation: '', minimumQuantity: 1, quantityStep: 1, description: '' }
  });

  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  const handleSubmit = () => {
    onSave({ ...formData, _newImageFiles: newImageFiles } as ProductVariant & { _newImageFiles?: File[] });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setNewImageFiles(prev => [...prev, ...files]);

      const urls = files.map(file => URL.createObjectURL(file));
      setPreviewUrls(prev => [...prev, ...urls]);
    }
  };

  const removeNewImage = (index: number) => {
    URL.revokeObjectURL(previewUrls[index]);
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
    setNewImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (imageIndex: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images?.filter((_, i) => i !== imageIndex) || []
    }));
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAttributeChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [field]: value
      }
    }));
  };

  const handlePackagingChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      packaging: {
        ...prev.packaging,
        [field]: value
      }
    }));
  };

  const handlePriceChange = (tarifa: TarifaKey, value: string) => {
    const numValue = value === '' ? 0 : parseFloat(value);
    setFormData(prev => ({
      ...prev,
      prices: {
        ...prev.prices,
        [tarifa]: numValue
      }
    }));
  };

  const handlePromoPriceChange = (tarifa: TarifaKey, value: string) => {
    const numValue = value === '' ? 0 : parseFloat(value);
    setFormData(prev => ({
      ...prev,
      promoPrices: {
        ...prev.promoPrices,
        [tarifa]: numValue
      }
    }));
  };

  return (
    <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
      <h4 className="text-lg font-medium text-gray-900 mb-4">
        {variant.name ? 'Editar Variante' : 'Nueva Variante'}
      </h4>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nombre *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">SKU *</label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => handleChange('sku', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Tarifa 1 - Retail/Minorista *</label>
              <div className="relative mt-1">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">EUR</span>
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  value={formData.prices?.tarifa1 || 0}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    handlePriceChange('tarifa1', e.target.value);
                    handleChange('price', value);
                  }}
                  className="block w-full pl-10 border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Precio con Descuento</label>
              <input
                type="number"
                step="0.001"
                min="0"
                value={formData.discountPrice || ''}
                onChange={(e) => handleChange('discountPrice', e.target.value ? parseFloat(e.target.value) : 0)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Stock *</label>
              <input
                type="number"
                min="0"
                value={formData.stock}
                onChange={(e) => handleChange('stock', parseInt(e.target.value))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Peso (gramos)</label>
              <input
                type="number"
                min="0"
                value={formData.weight || ''}
                onChange={(e) => handleChange('weight', e.target.value ? parseInt(e.target.value) : 0)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black"
              />
            </div>
          </div>

          <div className="space-y-4">
            <h5 className="text-md font-medium text-gray-900">Atributos</h5>

            <div>
              <label className="block text-sm font-medium text-gray-700">Tamano</label>
              <input
                type="text"
                value={formData.attributes?.size || ''}
                onChange={(e) => handleAttributeChange('size', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black"
                placeholder="ej: 1kg, 5kg, S, M, L"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Unidad</label>
              <input
                type="text"
                value={formData.attributes?.unit || ''}
                onChange={(e) => handleAttributeChange('unit', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black"
                placeholder="ej: kg, g, ml, unidades"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo de Envase</label>
              <input
                type="text"
                value={formData.attributes?.packaging || ''}
                onChange={(e) => handleAttributeChange('packaging', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black"
                placeholder="ej: Bolsa, Lata, Botella"
              />
            </div>

            <div className="space-y-3 mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h5 className="text-md font-medium text-blue-800">Empaquetado y Venta</h5>
              <p className="text-xs text-blue-600 mb-2">Define como se vende esta variante</p>

              <div>
                <label className="block text-sm font-medium text-blue-700">Presentacion</label>
                <input
                  type="text"
                  value={formData.packaging?.presentation || ''}
                  onChange={(e) => handlePackagingChange('presentation', e.target.value)}
                  className="mt-1 block w-full border-blue-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                  placeholder="ej: Saco de 25 kg, Caja expositora 6 uds"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-blue-700">Cantidad minima</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.packaging?.minimumQuantity || 1}
                    onChange={(e) => handlePackagingChange('minimumQuantity', parseInt(e.target.value) || 1)}
                    className="mt-1 block w-full border-blue-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-700">Incremento (step)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.packaging?.quantityStep || 1}
                    onChange={(e) => handlePackagingChange('quantityStep', parseInt(e.target.value) || 1)}
                    className="mt-1 block w-full border-blue-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-700">Descripcion de venta</label>
                <input
                  type="text"
                  value={formData.packaging?.description || ''}
                  onChange={(e) => handlePackagingChange('description', e.target.value)}
                  className="mt-1 block w-full border-blue-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                  placeholder="ej: Venta por pallets de 3 unidades"
                />
              </div>
            </div>

            {(userRole === 'admin' || userRole === 'beeadmin') && (
              <div className="space-y-3">
                <h5 className="text-md font-medium text-gray-900">Precios por Tarifa</h5>

                {NON_BASE_TARIFA_OPTIONS.map((tarifaOption) => (
                  <div key={tarifaOption.key}>
                    <label className="block text-sm font-medium text-gray-700">{tarifaOption.label}</label>
                    <div className="relative mt-1">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">EUR</span>
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={formData.prices?.[tarifaOption.key] || 0}
                        onChange={(e) => handlePriceChange(tarifaOption.key, e.target.value)}
                        className="block w-full pl-10 border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(userRole === 'admin' || userRole === 'beeadmin') && (
              <div className="space-y-3 mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <h5 className="text-md font-medium text-green-800">Promociones por Tarifa</h5>
                <p className="text-xs text-green-600 mb-2">Deja en 0 si no hay promocion para esa tarifa</p>

                {TARIFA_OPTIONS.map((tarifaOption) => (
                  <div key={tarifaOption.key}>
                    <label className="block text-sm font-medium text-green-700">{`Promo ${tarifaOption.label}`}</label>
                    <div className="relative mt-1">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-green-500">EUR</span>
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={formData.promoPrices?.[tarifaOption.key] || 0}
                        onChange={(e) => handlePromoPriceChange(tarifaOption.key, e.target.value)}
                        className="block w-full pl-10 border-green-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 bg-white"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Variante activa
              </label>
            </div>
          </div>
        </div>

        <div className="mt-6 border-t pt-4">
          <h5 className="text-md font-medium text-gray-900 mb-3">Imagenes de la Variante</h5>

          {formData.images && formData.images.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-2">Imagenes actuales:</p>
              <div className="grid grid-cols-4 gap-2">
                {formData.images.map((img, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={img.url}
                      alt={img.alt || 'Imagen de variante'}
                      className="w-full h-20 object-cover rounded-md border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeExistingImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {previewUrls.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-2">Nuevas imagenes (se subiran al guardar):</p>
              <div className="grid grid-cols-4 gap-2">
                {previewUrls.map((url, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={url}
                      alt={`Preview ${idx + 1}`}
                      className="w-full h-20 object-cover rounded-md border-2 border-blue-300"
                    />
                    <button
                      type="button"
                      onClick={() => removeNewImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Anadir imagenes</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
            />
            <p className="text-xs text-gray-500 mt-1">Las imagenes se subiran cuando guardes la variante</p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
          >
            Guardar Variante
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductVariantsForm;
